import { supabase } from '@/lib/supabase';
import { parseTranscription } from '@/lib/utils';

const BUCKET = 'content-audio';
/** Transcripts sit beside the recordings, in their own folder. */
const FOLDER = 'transcripts';

const CONTENT_TYPES: Record<string, string> = {
  json: 'application/json; charset=utf-8',
  // Plain text on purpose: the player reads the file with response.text(), and
  // application/x-subrip makes some browsers offer a download instead.
  srt: 'text/plain; charset=utf-8',
  vtt: 'text/vtt; charset=utf-8',
};

export interface UploadedTranscription {
  /** Public URL of the file -- the one to store on the config. */
  url: string;
  bytes: number;
  /** Lines the player will show, so the caller can say the file was readable. */
  lines: number;
}

function slugify(name: string): string {
  return (
    name
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/\.[^.]+$/, '')
      .replace(/[^a-zA-Z0-9._-]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 80) || 'transcriptie'
  );
}

/**
 * Puts one transcript in the content-audio bucket and hands back the URL.
 *
 * Transcripts used to be linked straight from Dropbox, which the player has to
 * fetch() while the audio is already running -- a dropped connection there means
 * the text silently never appears. Anything uploaded here is served from the
 * same bucket as the audio instead.
 *
 * The file is parsed before it is uploaded: a transcript that yields no lines
 * is the wrong file, and finding that out here beats finding out halfway
 * through a meditation. Writing to this bucket requires the admin role; the
 * storage policy enforces that.
 */
export async function uploadTranscription(
  file: File,
): Promise<UploadedTranscription> {
  const ext = (file.name.match(/\.([a-zA-Z0-9]+)$/)?.[1] || '').toLowerCase();
  if (!CONTENT_TYPES[ext]) {
    throw new Error('Kies een .json (met woordtijden), .srt of .vtt bestand.');
  }

  const text = await file.text();
  let lines = 0;
  try {
    lines = parseTranscription(text).length;
  } catch {
    lines = 0;
  }
  if (lines === 0) {
    throw new Error('Dit bestand bevat geen leesbare transcriptie.');
  }

  const stem = slugify(file.name);
  // Short suffix so re-uploading a corrected file never serves a cached copy
  // of the old one, and two people uploading the same name don't collide.
  const unique = Math.random().toString(16).slice(2, 10);
  const path = `${FOLDER}/${stem}-${unique}.${ext}`;

  const { error } = await supabase.storage
    .from(BUCKET)
    .upload(path, file, { contentType: CONTENT_TYPES[ext] });
  if (error) throw error;

  const {
    data: { publicUrl },
  } = supabase.storage.from(BUCKET).getPublicUrl(path);

  return { url: publicUrl, bytes: file.size, lines };
}
